import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, MessageSquare, ShieldOff, CheckCircle2, XCircle, Filter, MapPin, ShieldCheck } from 'lucide-react';
import { Card, Badge } from './AdminPanelPrimitives';
import { StatusChip, EmptyState } from './adminDesignSystem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = { open: 'red', in_review: 'orange', resolved: 'green', spurious: '' };
// 'active' is a client-side filter that combines open + in_review (the queue admins act on).
// Resolved & spurious reports stay out of this list (they remain visible in the heatmap above).
const ACTIVE_STATUSES = ['open', 'in_review'];

export const AdminVenueReportsPanel = ({ headers, onMessageReporter }) => {
  const [statusFilter, setStatusFilter] = useState('active');
  const [reports, setReports] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // 'active' & 'all' both need every status; we filter client-side for 'active'.
      const params = (statusFilter === 'all' || statusFilter === 'active') ? {} : { status: statusFilter };
      const [rRes, hRes] = await Promise.all([
        axios.get(`${API}/admin/venue-reports`, { headers, params }),
        axios.get(`${API}/admin/venue-reports/heatmap`, { headers }),
      ]);
      setReports(rRes.data || []);
      setHeatmap(hRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const updateStatus = async (row, newStatus) => {
    try {
      await axios.patch(`${API}/admin/venue-reports/${row.id}`, { status: newStatus }, { headers });
      toast.success(`Report marked ${newStatus}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const deactivateVenue = async (row) => {
    if (!window.confirm(`Move ${row.venue_name || 'this venue'} to Admin Hold? It will be hidden from guests and appear under Venues → Admin hold where you can release it later.`)) return;
    try {
      await axios.post(`${API}/admin/venue-reports/${row.id}/deactivate-venue`, {}, { headers });
      toast.success('Venue moved to Admin hold — release it from Venues → Admin hold');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move venue to hold');
    }
  };

  const stats = useMemo(() => {
    const counts = { open: 0, in_review: 0, resolved: 0, spurious: 0 };
    reports.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [reports]);

  // The "Active" view = open + in_review. Resolved/Spurious are intentionally hidden
  // from this list — they live in the heatmap above so admins can still spot
  // venues with patterns over time, but the queue itself stays focused on
  // reports that need triage.
  const visibleReports = useMemo(() => {
    if (statusFilter === 'active') {
      return reports.filter((r) => ACTIVE_STATUSES.includes(r.status));
    }
    return reports;
  }, [reports, statusFilter]);

  const activeCount = (stats.open || 0) + (stats.in_review || 0);

  return (
    <div data-testid="admin-venue-reports-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card style={{ padding: 18 }}>
        <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22 }}>Venue Reports</h2>
        <p style={{ margin: '4px 0 14px', fontSize: 13, color: '#666' }}>
          Reports submitted via the guest "Report a venue" flow. Drill into a venue from the heatmap, or filter by status below. Resolved &amp; spurious reports are hidden from the active queue but still count in the heatmap above.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { id: 'active', label: 'Active', tone: 'urgent', count: activeCount },
            { id: 'all', label: 'All', tone: 'neutral', count: reports.length },
            { id: 'open', label: 'Open', tone: 'urgent', count: stats.open || 0 },
            { id: 'in_review', label: 'In review', tone: 'early', count: stats.in_review || 0 },
            { id: 'resolved', label: 'Resolved', tone: 'active', count: stats.resolved || 0 },
            { id: 'spurious', label: 'Spurious', tone: 'resolved', count: stats.spurious || 0 },
          ].map((s) => (
            <StatusChip
              key={s.id}
              tone={s.tone}
              label={s.label}
              count={s.count}
              active={statusFilter === s.id}
              onClick={() => setStatusFilter(s.id)}
              dataTestId={`admin-venue-report-filter-${s.id}`}
            />
          ))}
        </div>
      </Card>

      {/* Heatmap */}
      <Card style={{ padding: 18 }} data-testid="admin-venue-reports-heatmap">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 18 }}>Heatmap by venue</h3>
          {heatmap.length > 0 ? (
            <span data-testid="admin-venue-reports-heatmap-count" style={{ fontSize: 12, color: '#888' }}>
              {heatmap.length} venue{heatmap.length === 1 ? '' : 's'}{heatmap.length > 10 ? ' · scroll to see more' : ''}
            </span>
          ) : null}
        </div>
        <p style={{ margin: '4px 0 12px', fontSize: 12, color: '#888' }}>Aggregated counts, sorted by total reports (most reported first). Higher totals = more concerning. Resolved/spurious are kept here so you can spot repeat offenders.</p>
        {heatmap.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            tone="active"
            title="No venues have any reports"
            body="When a guest reports a venue (spam, fraud, harassment, etc.) it shows up here grouped by venue with a heat colour. Open reports = red, in-review = yellow."
            dataTestId="admin-venue-reports-heatmap-empty-state"
          />
        ) : (
          <div data-testid="admin-venue-reports-heatmap-scroll" style={{ maxHeight: 360, overflowY: 'auto', overflowX: 'auto', border: '1px solid #F1F1F1', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#FFF', zIndex: 1, boxShadow: '0 1px 0 #E7E7E7' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#888' }}>Venue</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#888' }}>Total</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#DD0608' }}>Open</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#B56B11' }}>In review</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#00A246' }}>Resolved</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#888' }}>Spurious</th>
                  <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 11, textTransform: 'uppercase', color: '#888' }}>Last</th>
                </tr>
              </thead>
              <tbody>
                {heatmap.map((row) => {
                  // Heat scale: 0=neutral, 1-2=light, 3-5=medium, 6+=hot
                  const open = row.open || 0;
                  const bg = open >= 6 ? '#FDECEE' : open >= 3 ? '#FFF6E6' : open >= 1 ? '#FFFCEC' : '#FFFFFF';
                  return (
                    <tr key={row.venue_id} data-testid={`admin-venue-report-heat-${row.venue_id}`} style={{ background: bg, borderBottom: '1px solid #F1F1F1' }}>
                      <td style={{ padding: '8px 6px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <MapPin size={12} color="#666" /> {row.venue_name || row.venue_id}
                      </td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700 }}>{row.total}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', color: open > 0 ? '#DD0608' : '#666', fontWeight: open > 0 ? 700 : 500 }}>{open}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{row.in_review || 0}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{row.resolved || 0}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{row.spurious || 0}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right', fontSize: 11, color: '#888' }}>
                        {row.last_reported_at ? new Date(row.last_reported_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div data-testid="admin-venue-reports-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? <p style={{ textAlign: 'center', color: '#999' }}>Loading…</p> : null}
        {!loading && visibleReports.length === 0 ? (
          statusFilter === 'active' ? (
            <EmptyState
              icon={ShieldCheck}
              tone="active"
              title="Inbox is clear — no reports to triage"
              body="There are no open or in-review reports right now. Resolved and spurious reports are tucked away under their own filters so this view stays focused on what needs your attention."
              dataTestId="admin-venue-reports-empty-state"
            />
          ) : statusFilter === 'all' ? (
            <EmptyState
              icon={ShieldCheck}
              tone="active"
              title="No venue reports — everyone behaving"
              body="Guests can flag a venue from the guest map via the 'Report this venue' link. Reports show up here so you can review them, mark them spurious, resolve them, or hold the venue."
              dataTestId="admin-venue-reports-empty-state"
            />
          ) : (
            <EmptyState
              icon={Filter}
              tone="neutral"
              title={`Nothing in “${statusFilter}”`}
              body={`No reports currently match the “${statusFilter}” filter. Switch filters to see other states.`}
              actionLabel="Back to active queue"
              onAction={() => setStatusFilter('active')}
              dataTestId="admin-venue-reports-empty-state"
            />
          )
        ) : null}
        {visibleReports.map((row) => (
          <Card key={row.id} data-testid={`admin-venue-report-row-${row.id}`} style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700 }}>
                  <Flag size={14} style={{ marginRight: 6, verticalAlign: -2 }} color="#DD0608" />
                  {row.venue_name || row.venue_id}
                </p>
                <p style={{ margin: '4px 0', fontSize: 12, color: '#888' }}>
                  Issue: <strong>{row.issue}</strong> · by {row.reporter_email || 'anonymous'} · {new Date(row.created_at).toLocaleString()}
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: '#444', whiteSpace: 'pre-wrap' }}>{row.details}</p>
                {row.admin_notes ? <p style={{ margin: '6px 0 0', fontSize: 12, color: '#666', fontStyle: 'italic' }}>Admin: {row.admin_notes}</p> : null}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <Badge text={row.status} color={STATUS_COLORS[row.status]} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {row.status !== 'in_review' ? (
                    <button onClick={() => updateStatus(row, 'in_review')} data-testid={`admin-venue-report-in-review-${row.id}`}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><Filter size={12} /> In review</button>
                  ) : null}
                  {row.status !== 'resolved' ? (
                    <button onClick={() => updateStatus(row, 'resolved')} data-testid={`admin-venue-report-resolve-${row.id}`}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #00C853', background: '#E8F9EF', color: '#00A246', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700 }}><CheckCircle2 size={12} /> Resolve</button>
                  ) : null}
                  {row.status !== 'spurious' ? (
                    <button onClick={() => updateStatus(row, 'spurious')} data-testid={`admin-venue-report-spurious-${row.id}`}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><XCircle size={12} /> Spurious</button>
                  ) : null}
                  {row.reporter_id && typeof onMessageReporter === 'function' ? (
                    <button onClick={() => onMessageReporter(row.reporter_id, row.reporter_email || 'Reporter')} data-testid={`admin-venue-report-message-${row.id}`}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}><MessageSquare size={12} /> Message reporter</button>
                  ) : null}
                  <button onClick={() => deactivateVenue(row)} data-testid={`admin-venue-report-deactivate-${row.id}`}
                    style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #EAB308', background: '#FEF9C3', color: '#854D0E', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700 }}><ShieldOff size={12} /> Move to On Hold</button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminVenueReportsPanel;
